import {
  moveTargetsResponseSchema,
  moveTaskResponseSchema,
  taskListResponseSchema,
  taskLoadPreviewResponseSchema,
  taskSchema,
  trashResponseSchema,
  type CreateTaskBody,
  type TaskLoadPreviewBody,
  type BoardMoveBody,
  type MoveTaskBody,
  type TaskListQuery,
  type UpdateTaskBody,
} from '@pm-tool/shared';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiRequest } from '@/lib/api-client';

/**
 * Query keys. Project lists live below `['projects', id]` (invalidated with the project), single
 * tasks below `['tasks']`.
 */
export const taskKeys = {
  all: ['tasks'] as const,
  detail: (id: string) => ['tasks', id] as const,
  byKey: (key: string) => ['tasks', 'by-key', key] as const,
  moveTargets: (id: string) => ['tasks', id, 'move-targets'] as const,
  list: (projectId: string) => ['projects', projectId, 'tasks'] as const,
  trash: (projectId: string) => ['projects', projectId, 'trash'] as const,
  /** Below the list: every task change in the project refreshes the hint (RES-14). */
  loadPreview: (projectId: string, body: TaskLoadPreviewBody) =>
    ['projects', projectId, 'tasks', 'load-preview', body] as const,
};

/** TASK-04: the whole filtered list (at most TASK_LIST_LIMIT tasks). */
export const projectTasksQuery = (projectId: string, query: Partial<TaskListQuery> = {}) =>
  queryOptions({
    queryKey: [...taskKeys.list(projectId), query],
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/v1/projects/${projectId}/tasks?${toSearchParams(query).toString()}`,
        schema: taskListResponseSchema,
        signal,
      }),
  });

/** Query string of the list: lists comma-separated, empty values left out. */
export function toSearchParams(query: Partial<TaskListQuery>): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
      continue;
    }
    params.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  return params;
}

export const taskByKeyQuery = (key: string) =>
  queryOptions({
    queryKey: taskKeys.byKey(key),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/v1/tasks/by-key/${encodeURIComponent(key)}`,
        schema: taskSchema,
        signal,
      }),
    retry: false,
  });

export const moveTargetsQuery = (taskId: string) =>
  queryOptions({
    queryKey: taskKeys.moveTargets(taskId),
    queryFn: ({ signal }) =>
      apiRequest({
        path: `/v1/tasks/${taskId}/move-targets`,
        schema: moveTargetsResponseSchema,
        signal,
      }),
  });

export const trashQuery = (projectId: string) =>
  queryOptions({
    queryKey: taskKeys.trash(projectId),
    queryFn: ({ signal }) =>
      apiRequest({ path: `/v1/projects/${projectId}/trash`, schema: trashResponseSchema, signal }),
  });

/** RES-14: would these values overbook the assignees? Read only. */
export const loadPreviewQuery = (projectId: string, body: TaskLoadPreviewBody) =>
  queryOptions({
    queryKey: taskKeys.loadPreview(projectId, body),
    queryFn: ({ signal }) =>
      apiRequest({
        method: 'POST',
        path: `/v1/projects/${projectId}/tasks/load-preview`,
        schema: taskLoadPreviewResponseSchema,
        body,
        signal,
      }),
  });

const noContent = z.unknown();

export const taskApi = {
  create: (projectId: string, body: CreateTaskBody) =>
    apiRequest({
      method: 'POST',
      path: `/v1/projects/${projectId}/tasks`,
      schema: taskSchema,
      body,
    }),
  update: (taskId: string, body: UpdateTaskBody) =>
    apiRequest({ method: 'PATCH', path: `/v1/tasks/${taskId}`, schema: taskSchema, body }),
  watch: (taskId: string) =>
    apiRequest({ method: 'PUT', path: `/v1/tasks/${taskId}/watchers/me`, schema: taskSchema }),
  unwatch: (taskId: string) =>
    apiRequest({ method: 'DELETE', path: `/v1/tasks/${taskId}/watchers/me`, schema: taskSchema }),
  move: (taskId: string, body: MoveTaskBody) =>
    apiRequest({
      method: 'POST',
      path: `/v1/tasks/${taskId}/move`,
      schema: moveTaskResponseSchema,
      body,
    }),
  remove: (taskId: string) =>
    apiRequest({ method: 'DELETE', path: `/v1/tasks/${taskId}`, schema: noContent }),
  boardMove: (taskId: string, body: BoardMoveBody) =>
    apiRequest({
      method: 'POST',
      path: `/v1/tasks/${taskId}/board-move`,
      schema: taskSchema,
      body,
    }),
  restore: (taskId: string) =>
    apiRequest({ method: 'POST', path: `/v1/tasks/${taskId}/restore`, schema: taskSchema }),
};
